import { ALLOWED_IMAGE_TYPES, BADGE_DATE_TIME_FORMAT, DISPLAY_STATE_COUNTRIES } from '../../common/constants';
import { IMAGE } from '../../types/enums';
import moment from 'moment';
import replace from 'lodash/replace';
import { v4 } from 'uuid';
import { CodeName, Nullable } from '../../types/common';
import EventDetailsService from '../../services/EventDetailsService';

const findAllowedTypeInContentType = (contentType: string) => ALLOWED_IMAGE_TYPES.find((type) => contentType.includes(type));

const sanitiseName = (str: string) => {
  return replace(str, /[^\p{L}0-9 ]/gu, '').trim();
};

const getDisplayName = (firstName: string, lastName: string) => {
  return `${sanitiseName(firstName)}-${sanitiseName(lastName)}`;
};

const getPDFName = (firstName: string, lastName: string) => {
  return `${getDisplayName(firstName, lastName)}_badge_${moment().format(BADGE_DATE_TIME_FORMAT)}.pdf`;
};

const getUrlFromExistingImageUrl = (logoUrlSplit: Array<string>, imageDomainUrl: string) => {
  let logoUrl = '';
  for (let i = 1; i < 6; i++) {
    logoUrl += `/${logoUrlSplit[i]}`;
  }
  return `${imageDomainUrl}${logoUrl}`;
};

const getPortalImageDomain = async (eventEditionDomain: string, imageDomainUrl: string) => {
  const response = await EventDetailsService.getEventDetails(eventEditionDomain);
  const logoUrlSplit = response.siteDetails.logo.url.split('/');
  return getUrlFromExistingImageUrl(logoUrlSplit, imageDomainUrl);
};

const getPortalImageDomainFromExistingLogoUrl = (logoUrl: string, imageDomainUrl: string) => {
  const logoUrlSplit = logoUrl.split('/');
  return getUrlFromExistingImageUrl(logoUrlSplit, imageDomainUrl);
};

const getPortalImageUrl = (portalImageDomain: string, imageName: string, sku: string) => {
  return `${portalImageDomain}/badge/${sku.toLowerCase()}/${imageName}.jpg/_jcr_content/renditions/original./${v4()
    .slice(0, 6)}/${imageName}.jpg`;
};

const getVisaImageUrl = (portalImageDomain: string, imageName: string) => {
  return `${portalImageDomain}/visa/${imageName}.jpg/_jcr_content/renditions/original./${v4()
    .slice(0, 6)}/${imageName}.jpg`;
};

const getIsBadgeDoubleSided = async (imageDomain: string, sku: string) => {
  const url = getPortalImageUrl(imageDomain, IMAGE.image1, sku);
  if (url) {
    try {
      const res = await fetch(url);
      return !Boolean(findAllowedTypeInContentType(res.headers.get('content-type')!));
    } catch (e) {
      console.error('Error while getting the image1: ', e);
      return true;
    }
  }
  return true;
};

const getCountryName = (countries: Array<CodeName>, countryCode: string | undefined, state: Nullable<string>) => {
  if (!countryCode) return;
  if (!DISPLAY_STATE_COUNTRIES.includes(countryCode) || !state) {
    return countries.find(({ code }) => countryCode === code)?.name;
  }
};

const getCountryList = async (url: string, clientId: string, locale = 'en-GB') => {
  try {
    const query = `query { formDefinitionCountry(locale: "${locale}") { code, name } }`;
    const response = ((await fetch(`${url}/graphql`, {
      method: 'POST',
      body: query,
      headers: {
        'Content-Type': 'application/graphql',
        Accept: 'application/json',
        'x-clientid': clientId
      }
    })) as unknown) as { _bodyBlob: Blob };
    const parsedData = await response._bodyBlob.text();
    return JSON.parse(parsedData).data.formDefinitionCountry as Array<CodeName>;
  } catch (err) {
    console.error(err);
    return [];
  }
};

const BadgeUtils = {
  findAllowedTypeInContentType,
  getPDFName,
  getPortalImageUrl,
  getIsBadgeDoubleSided,
  getCountryName,
  getCountryList,
  getPortalImageDomain,
  getPortalImageDomainFromExistingLogoUrl,
  getVisaImageUrl
};

export default BadgeUtils;
